/**
 * Razorpay Payment Service - SECURE VERSION
 * Payment verification now happens on BACKEND (Cloud Functions)
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import logger from './logger';

// Initialize Firebase Functions
const functions = getFunctions();

// Load Razorpay script dynamically
export const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/**
 * Create Razorpay order (BACKEND)
 * This is now SECURE - runs on server
 */
export const createRazorpayOrder = async ({ amount, orderId, customerInfo }) => {
  try {
    const createOrder = httpsCallable(functions, 'createRazorpayOrder');
    
    const result = await createOrder({
      amount,
      orderId,
      customerInfo
    });

    return result.data;
  } catch (error) {
    logger.error('Failed to create Razorpay order:', error);
    throw error;
  }
};

/**
 * Initialize Razorpay payment
 */
export const initiateRazorpayPayment = async ({
  amount,
  orderId,
  customerInfo,
  onSuccess,
  onFailure
}) => {
  try {
    // Load Razorpay script
    const isScriptLoaded = await loadRazorpayScript();
    if (!isScriptLoaded) {
      throw new Error('Failed to load Razorpay SDK');
    }

    // Create order on backend first
    const { razorpayOrderId } = await createRazorpayOrder({
      amount,
      orderId,
      customerInfo
    });

    const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;

    const options = {
      key: razorpayKey,
      amount: amount * 100,
      currency: 'INR',
      name: 'Odisha Zaika',
      description: `Order #${orderId}`,
      image: '/logo192.png',
      order_id: razorpayOrderId, // Backend-generated order ID
      handler: async function (response) {
        // Verify payment on BACKEND
        try {
          const verifyResult = await verifyPayment({
            orderId,
            paymentId: response.razorpay_payment_id,
            razorpayOrderId: response.razorpay_order_id,
            signature: response.razorpay_signature
          });

          if (verifyResult.verified) {
            onSuccess(response);
          } else {
            onFailure({ error: 'Payment verification failed' });
          }
        } catch (error) {
          onFailure({ error: error.message });
        }
      },
      prefill: {
        name: customerInfo.name,
        contact: customerInfo.phone,
        email: customerInfo.email || ''
      },
      theme: { color: '#FFC124' },
      modal: {
        ondismiss: () => onFailure({ error: 'Payment cancelled' })
      }
    };

    const razorpay = new window.Razorpay(options);
    razorpay.on('payment.failed', (response) => {
      onFailure({
        error: response.error.description,
        code: response.error.code
      });
    });

    razorpay.open();
  } catch (error) {
    logger.error('Payment initiation error:', error);
    onFailure({ error: error.message });
  }
};

/**
 * Verify payment - SECURE BACKEND VERIFICATION
 * This now calls Cloud Function instead of client-side verification
 */
export const verifyPayment = async (paymentData) => {
  try {
    logger.info('🔒 Verifying payment on backend (SECURE)...');

    const verifyPaymentFunction = httpsCallable(functions, 'verifyPayment');
    
    const result = await verifyPaymentFunction({
      orderId: paymentData.orderId,
      paymentId: paymentData.paymentId,
      razorpayOrderId: paymentData.razorpayOrderId,
      signature: paymentData.signature
    });

    logger.info('✅ Payment verification result:', result.data);

    return {
      verified: result.data.verified,
      paymentId: result.data.paymentId
    };
  } catch (error) {
    logger.error('❌ Payment verification failed:', error);
    return {
      verified: false,
      error: error.message
    };
  }
};
